import React, {ReactElement, useCallback, useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {AuthorizationCode} from '../../../app/types'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import TextInput from '../../../common/form/TextInput'
import FormRow from '../../../common/form/FormRow'
import FormFieldBox from '../../../common/form/FormFieldBox'
import SpinnableSubmitButton from '../../../common/form/SpinnableSubmitButton'
import {useFormSubmission} from '../../../common/servercall/useFormSubmission'
import ErrorAlert from '../../../common/components/ErrorAlert'
import {UseFormSetValue} from 'react-hook-form/dist/types/form'
import {api} from "../../../common/api";
import {useUrlHelpers} from "../../../common/util/hooks";

const schema = yup
    .object()
    .shape({
        code: yup
            .string()
            .trim()
            .required('required-m')
            .matches(/^\d{5}$/, 'invalid-authorization-code'),
        description: yup
            .string()
            .trim()
            .max(80, 'too-long')
            .nullable(),
        ncos: yup
            .string()
            .trim()
            .required('required-m')
            .nullable()
    })
    .required()

type Props = {
    entry: AuthorizationCode | null
    onSubmit: (formData: AuthorizationCode) => void
}

export type ReturnProps = {
    handleSubmit: () => void
    form: () => ReactElement
    submitButton: () => ReactElement
    reset: () => void
    setValue: UseFormSetValue<AuthorizationCode>
}

const useAuthorizationCodeForm = ({ entry, onSubmit }: Props): ReturnProps => {
    const { t } = useTranslation()
    const {createUrl} = useUrlHelpers();
    const [allowedNcos, setAllowedNcos] = React.useState<string[]>([]);

    const { control, handleSubmit, setError, reset, setValue } = useForm<AuthorizationCode>({
        resolver: yupResolver(schema),
        defaultValues: entry
            ? {
                  ...entry,
              }
            : {},
    })

    const { submit, isSubmitting, serverError, onError } = useFormSubmission<AuthorizationCode>(onSubmit, setError)

    const fetchAllowedNcos = useCallback(
        async () => {
            const { data } = await api.get(createUrl('authorization-codes/allowed-ncos'))
            setAllowedNcos(data)
        }, [createUrl]
    )

    useEffect(() => {
        async function fetch() {
            await fetchAllowedNcos()
        }
        fetch()
    }, [fetchAllowedNcos])

    const ncosOptions = useMemo(
            () => allowedNcos.map((ncos: string) => ({label: t(`users.ncos-names.${ncos}`), value: ncos}))
        , [t, allowedNcos]
    )

    return {
        setValue: setValue,
        reset: reset,
        handleSubmit: handleSubmit(submit, onError),
        form: () =>
            entry ? (
                <>
                    <ErrorAlert errorKey={typeof serverError === 'string' ? serverError : undefined} showAlert={!!serverError} />
                    <FormRow>
                        <FormFieldBox fullWidth={true}>
                            <TextInput autofocus={true} label={t('contact-center-codes.code')} name={'code'} control={control} required={true} readonly={!!entry.uuid} />
                        </FormFieldBox>
                        <FormFieldBox fullWidth={true}>
                            <TextInput label={t('contact-center-codes.description')} name={'description'} control={control} required={false} />
                        </FormFieldBox>
                        <FormFieldBox fullWidth={true}>
                            <TextInput label={t('users.edit.ncos')} name={'ncos'} control={control} required={true} options={ncosOptions} />
                        </FormFieldBox>
                    </FormRow>
                </>
            ) : (
                <div />
            ),
        submitButton: () => (entry ? <SpinnableSubmitButton label={t(entry.uuid ? 'general.form-update' : 'general.form-create')} showSpinner={isSubmitting} /> : <></>),
    }
}

export default useAuthorizationCodeForm
